import { Autocomplete, Box, TextField } from '@mui/material';
import { useEffect, useState, forwardRef, useImperativeHandle, SyntheticEvent } from 'react';
import { AutocompleteChangeReason, AutocompleteChangeDetails } from '@mui/material/Autocomplete';
import usePlacesAutocomplete from 'use-places-autocomplete';
import { AutocompleteLocationProps, GooglePrediction } from './types';
import { parseAddressFromLocationType } from './hooks/parseAddressFromLocationType';

export type AutocomplateLocationRef = {
  clear: () => void;
};

export const AutocompleteLocation = forwardRef<AutocomplateLocationRef, AutocompleteLocationProps>(
  ({ onChange, defaultValue, values, setParentValue, disabled }, ref) => {
    const [selectedValue, setSelectedValue] = useState<GooglePrediction | null>(null);
    const [initialDefaultSet, setInitialDefaultSet] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [isUnverified, setIsUnverified] = useState(false);

    useEffect(() => {
      if (values?.location?.street && selectedValue == null && setParentValue) {
        setParentValue('location.street', '');
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values?.location?.street, selectedValue]);

    const {
      setValue,
      suggestions: { data },
    } = usePlacesAutocomplete({
      requestOptions: { types: ['geocode'] },
      debounce: 300,
      cache: 3600 * 12,
    });

    useImperativeHandle(ref, () => ({
      clear: () => {
        setSelectedValue(null);
        setValue('');
        setInputValue('');
        setIsUnverified(false);
        onChange?.(null);
      },
    }));

    useEffect(() => {
      if (defaultValue && !initialDefaultSet) {
        setValue(defaultValue);
        const mockPrediction: GooglePrediction = {
          description: defaultValue,
          place_id: 'default',
          structured_formatting: {
            main_text: defaultValue,
            secondary_text: '',
          },
          terms: [],
          types: [],
        };
        setSelectedValue(mockPrediction);
        setInputValue(defaultValue);
        setInitialDefaultSet(true);
      }
    }, [defaultValue, initialDefaultSet, setValue]);

    // Commits a free-text address that Google could not validate. Only the street
    // is propagated (isManual); city/state/coords are left untouched so a manual
    // street edit never wipes values the user typed into the other fields.
    const commitManualAddress = (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      const mockPrediction: GooglePrediction = {
        description: trimmed,
        place_id: 'custom',
        structured_formatting: {
          main_text: trimmed,
          secondary_text: '',
        },
        terms: [],
        types: [],
      };
      setSelectedValue(mockPrediction);
      setInputValue(trimmed);
      setIsUnverified(true);
      onChange?.({
        description: trimmed,
        address: trimmed,
        city: '',
        state: '',
        country: '',
        zipcode: null,
        coordinates: null,
        timeZoneInfo: undefined,
        isManual: true,
      });
    };

    const handleChange = async (
      event: SyntheticEvent<Element, Event>,
      value: string | GooglePrediction | null,
      reason: AutocompleteChangeReason,
      details?: AutocompleteChangeDetails<GooglePrediction>
    ) => {
      if (typeof value === 'string') {
        commitManualAddress(value);
        return;
      }

      setSelectedValue(value);

      if (!value) {
        setIsUnverified(false);
        onChange?.(null);
        return;
      }

      // A real Google prediction was selected: it is verified, clear the warning.
      setIsUnverified(false);

      const { description } = value;
      const { address, city, state, country, zipcode, coordinates, timeZoneInfo } =
        await parseAddressFromLocationType({ newValue: value });

      onChange?.({
        description,
        address,
        city,
        state,
        country,
        zipcode,
        coordinates,
        timeZoneInfo,
      });
    };

    // MUI freeSolo does not fire onChange on blur, so a typed address that is never
    // Enter-confirmed or picked from the dropdown would otherwise be lost. On blur we
    // commit it as a manual entry, unless it already matches the current selection.
    const handleBlur = () => {
      const trimmed = inputValue.trim();
      if (!trimmed) return;
      const currentLabel = selectedValue
        ? (selectedValue.structured_formatting?.main_text || selectedValue.description).trim()
        : '';
      if (trimmed === currentLabel) return;
      commitManualAddress(trimmed);
    };

    return (
      <Autocomplete
        id="location-autocomplete"
        forcePopupIcon={false}
        disabled={disabled}
        options={data}
        getOptionLabel={(option: GooglePrediction | string) => {
          if (typeof option === 'string') return option;
          return option?.structured_formatting?.main_text || option?.description || '';
        }}
        renderOption={(props, option: GooglePrediction) => (
          <Box component="li" {...props}>
            <Box>{option.description}</Box>
          </Box>
        )}
        isOptionEqualToValue={(option: GooglePrediction, value: GooglePrediction) =>
          option?.place_id === value?.place_id || option?.description === value?.description
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Street"
            helperText={
              isUnverified
                ? 'This address could not be verified by Google. You can still save it.'
                : undefined
            }
            FormHelperTextProps={{ sx: { color: 'warning.main' } }}
          />
        )}
        onInputChange={(event, newValue) => {
          setValue(newValue);
          setInputValue(newValue);
        }}
        onChange={handleChange}
        onBlur={handleBlur}
        value={selectedValue}
        freeSolo
      />
    );
  }
);
